import React, { useState } from 'react';
import { useTypedTranslation } from '@app/hooks';
import { IGrants, IThumbprint } from '@app/models';
import { numberOfDevices, isValidTrustedDevice, TRUSTED_DEVICE_NAME } from '@app/components/Common/trustedDevices.shared';
import AddLocationIcon from '@material-ui/icons/AddLocation';
import { DATE_TIME_FULL_FORMAT, passlyFormatDate } from '@app/helpers';
import { usersService, myProfileService } from '@app/services';
import { PasslyButton } from '..';

interface IProps {
    grantsInput: IGrants[];
    isMyProfile: boolean;
    userId: string;
}

export const TrustedDevices: React.SFC<IProps> = ({ grantsInput, isMyProfile, userId }) => {
    const { translation } = useTypedTranslation();

    const [grants, setGrants] = useState<IGrants[]>(grantsInput);

    const getTrustedDeviceOSImage = (grant: IGrants) => {
        if (grant.Type === TRUSTED_DEVICE_NAME) {
            if (grant.Thumbprint !== undefined) {
                let os: string = grant.ThumbprintAsObject.operatingSystem;

                if (!os) {
                    return '/assets/images/ua/os.png';
                }

                os = os.replace(' ', '').toLowerCase();

                return '/assets/images/ua/' + os + '.png';
            }
        }
        return '';
    };

    const getTrustedDeviceOS = (grant: IGrants) => {
        if (grant.Type === TRUSTED_DEVICE_NAME) {
            if (grant.Thumbprint !== undefined) {
                let t: IThumbprint = grant.ThumbprintAsObject;
                let os: string = '';

                if (t.device !== '' && t.device !== 'Other') {
                    os = t.device;

                    if (t.brand !== undefined && os.indexOf(t.brand) < 0 && t.brand !== '' && t.brand !== 'Other') {
                        os = t.brand + ' ' + os;
                    }
                } else {
                    os = t.operatingSystem;
                }

                return os;
            }
        }

        return '';
    };

    const trustedDeviceIsCurrentDevice = (grant: IGrants) => {
        if (grant.Type === TRUSTED_DEVICE_NAME) {
            if (!document.cookie) {
                return false;
            }

            let cookieName = '\\.tdic';

            let matched = document.cookie.match('(^|;)\\s*' + cookieName + '\\s*=\\s*([^;]+)');

            if (!matched || !matched.pop) {
                return false;
            }

            let cookie = matched.pop();

            return cookie === grant.ThumbprintAsObject.deviceIdentifier;
        }
    };

    const trustedDeviceGetLocation = (grant: IGrants) => {
        if (grant.Type === TRUSTED_DEVICE_NAME) {
            const clientIp: IThumbprint = grant.ThumbprintAsObject;

            let loc: string = '';

            if (clientIp !== undefined) {
                let c: number = 0;

                if (clientIp.city !== undefined) {
                    c++;
                    loc += clientIp.city;
                }

                if (clientIp.state !== undefined && clientIp.state !== clientIp.city) {
                    c++;

                    loc += ', ' + clientIp.state;
                }

                if ((loc === '' && clientIp.country !== undefined) || (clientIp.country !== undefined && c < 2)) {
                    loc += ', ' + clientIp.country;
                }

                if (loc === '') {
                    loc = translation('UNKNOWN_LOCATION');
                }
            }

            return loc;
        }
    };

    const trustedDeviceGetLastSeen = (grant: IGrants) => {
        if (grant.Type === TRUSTED_DEVICE_NAME) {
            if (!grant.RecentActivity) {
                return translation('UNKNOWN_TIME');
            }

            const activities: any = JSON.parse(grant.RecentActivity);

            if (!activities || !activities.length) {
                return translation('UNKNOWN_TIME');
            }

            let last = activities[0];

            if (!last.timestamp) {
                return translation('UNKNOWN_TIME');
            }

            const dateToFormat = new Date(last.timestamp);

            return passlyFormatDate(dateToFormat, DATE_TIME_FULL_FORMAT);
        }
    };

    const isTrustedDevice = (grant: IGrants) => {
        if (isValidTrustedDevice(grant)) {
            return true;
        }
        else {
            return false;
        }
    };

    const getTrustedDeviceBrowserImage = (grant: IGrants) => {
        let browser = grant.ThumbprintAsObject.application.replace(' ', '').toLowerCase();

        return '/assets/images/ua/' + browser + '.png';
    };

    const getTrustedDeviceBrowser = (grant: IGrants) => {
        let browser = grant.ThumbprintAsObject.application;

        if (grant.ThumbprintAsObject.version !== undefined) {
            browser = browser + ' ' + grant.ThumbprintAsObject.version;
        }

        return browser;
    };

    const removeGrantFromView = (grantId: string) => {
        let filteredGrants = grants.filter(function (grant) {
            if (grant.Id !== grantId) {
                return grant;
            }
        });
        setGrants(filteredGrants);
    };

    const stopTrusting = (grantId: string) => {
        if (isMyProfile) {
            // delete from my profile
            myProfileService.deleteGrant(userId, grantId)
                .then(() => {
                    removeGrantFromView(grantId);
                });
        }
        else {
            // delete from user detail
            usersService.deleteGrant(userId, grantId)
                .then(() => {
                    removeGrantFromView(grantId);
                });
        }
    };

    return (
        <>
            <div className='detailPage__section'>
                <div className='detailPage__section-head'>
                    {translation('TRUSTED_DEVICES_SECTION')}
                </div>

                <div className='detailPage__section-content'>
                    {numberOfDevices(grants) === 0 ?
                        (
                            <p>{translation('EMPTY_DEVICE_LIST')}</p>
                        )
                        :
                        (
                            <div>
                                {grants.map((item: IGrants, index: number) => (
                                    <div key={index}>
                                        {isTrustedDevice(item) &&
                                            <ul className='detailPage__section-content-devices'>
                                                <li>
                                                    <img className='detailPage__section-content-icon' src={getTrustedDeviceOSImage(item)}></img>
                                                    <span className='detailPage__section-content-devices-label'>{getTrustedDeviceOS(item)}</span>
                                                </li>

                                                <li>
                                                    <div className='detailPage__section-content-icon'><AddLocationIcon fontSize={'inherit'} /></div>
                                                    <div className='detailPage__section-content-devices-label'>
                                                        <span className='d__block'>{trustedDeviceGetLocation(item)}</span>
                                                        <span>{trustedDeviceGetLastSeen(item)}</span>
                                                    </div>
                                                </li>

                                                <li>
                                                    <img className='detailPage__section-content-icon' src={getTrustedDeviceBrowserImage(item)}></img>
                                                    <span className='detailPage__section-content-devices-label'>{getTrustedDeviceBrowser(item)}</span>
                                                </li>

                                                <li>
                                                    <span className='detailPage__section-content-icon'>&nbsp;</span>
                                                    {trustedDeviceIsCurrentDevice(item) &&
                                                        <span className='detailPage__section-content-devices-label'>{translation('IS_CURRENT_DEVICE')}</span>
                                                    }
                                                </li>

                                                <li>
                                                    <PasslyButton variant='contained' color='primary' onClick={() => stopTrusting(item.Id)}>
                                                        {translation('REVOKE_DEVICE')}
                                                    </PasslyButton>
                                                </li>

                                            </ul>
                                        }
                                    </div>
                                ))}
                            </div>
                        )
                    }
                </div>
            </div>
        </>
    );
};
